import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAppContext } from "../AppContext";

import useAxiosFetch from "../common/useAxiosFetch";
import LoadPage from "../common/LoadPage";
import Chart from "../common/Chart";
import { getRiskSeverity, getScoreBorderClass, getRiskColorVar } from "../common/helpers";
const moment = require('moment');

const TEMP_ACCEPT_THRESHOLD_DAYS = 30;
const ATTENTION_THRESHOLD_DAYS = 60;
const OWNER_FILTERS = ['all', 'high', 'medium', 'low'];

// Safely parse a value as integer, returning 0 for undefined/null/NaN
const safeInt = (val) => {
  const parsed = parseInt(val);
  return isNaN(parsed) ? 0 : parsed;
};

// Calculate score from chance and impact, returning null if either is missing
const safeScore = (chance, impact) => {
  const c = parseInt(chance);
  const i = parseInt(impact);
  if (isNaN(c) || isNaN(i)) return null;
  return c * i;
};

const calculateRiskScore = (item, riskData, rangeBounds) => {
  const chances = ['initial-chance', 'actual-chance', 'expected-chance'];
  const impacts = ['initial-impact', 'actual-impact', 'expected-impact'];

  for (let i = 0; i < 3; i++) {
    let score = safeScore(item[chances[i]], item[impacts[i]]);
    if (score === null) continue;
    riskData[i][getRiskSeverity(score, rangeBounds)]++;
  }
};

const calculateRiskLevels = (risks, duration, unit, rangeBounds) => {
  let startDate = moment().subtract(duration, unit).startOf(unit);
  let endDate = moment().startOf(unit);
  let result = {};

  risks.forEach(risk => {
    let lastScore = safeInt(risk["initial-chance"]) * safeInt(risk["initial-impact"]);
    let lastUpdate = moment(risk.date);
    
    // Sort the update-reasons array
    risk["update-reasons"].sort((a, b) => moment(a.date).isBefore(b.date) ? -1 : 1);

    let currentPeriod = moment(startDate);

    while (currentPeriod.isBefore(endDate)) {
      let period = currentPeriod.format(unit === 'months' ? 'YYYY-MM' : 'YYYY-MM-DD');
      result[period] = result[period] || { risks: 0, totalRiskScore: 0, low: 0, medium: 0, high: 0 };

      // Update the lastScore if there's an update for the current period
      for (let update of risk["update-reasons"]) {
        let updateDate = moment(update.date);
        if (updateDate.isAfter(lastUpdate) && updateDate.isSameOrBefore(currentPeriod)) {
          lastUpdate = updateDate;
          lastScore = safeInt(update.chance) * safeInt(update.impact);
        }
      }

      if (lastUpdate.isSameOrBefore(currentPeriod)) {
        result[period].risks++;
        result[period].totalRiskScore += lastScore; 
        result[period][getRiskSeverity(lastScore, rangeBounds)]++;
      }

      currentPeriod = currentPeriod.add(1, unit);
    }
  });

  return result;
};

const calculateRiskHistory = (risks, duration, unit, trends, rangeBounds) => {
  const riskHistory = calculateRiskLevels(risks, duration, unit, rangeBounds);

  let riskHistoryData = [];
  
  Object.keys(riskHistory).forEach(key => {
    riskHistoryData.push({
      name: key,
      risks: riskHistory[key].risks, 
      totalRiskScore: riskHistory[key].totalRiskScore,
      avgRiskScore: (riskHistory[key].totalRiskScore/(riskHistory[key].risks || 1)).toFixed(2),
      low: riskHistory[key].low,
      medium: riskHistory[key].medium,
      high: riskHistory[key].high
    });
  });

  // Calculate totalRiskScore and avgRiskScore
  let totalRiskScore = risks.reduce((total, risk) => total + (safeInt(risk['actual-impact']) * safeInt(risk['actual-chance'])), 0);
  let avgRiskScore = (totalRiskScore / (risks.length || 1)).toFixed(2);

  // Add current day to riskHistoryData
  riskHistoryData.push({
    name: moment().format(unit === 'months' ? 'YYYY-MM' : 'YYYY-MM-DD'),
    risks: risks.length,
    totalRiskScore,
    avgRiskScore,
    low: trends.low,
    medium: trends.medium,
    high: trends.high
  });

  return riskHistoryData;
};

const ArchivedToggle = ({ active, onToggle, t, style }) => (
  <div
    role="button"
    tabIndex={0}
    aria-pressed={active}
    title={t('show_archived_risks')}
    className={`action-btn content-bubble-btn ${active ? "action-btn-active" : ""}`}
    style={style}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    }}
  >
    <i className="icon-archive" /> {t('archived')}
  </div>
);

const RiskControlTimeScale = ({ riskControlScale, onScaleChange, onSinceStartClick, t, isSinceStartMode, showArchived, onToggleArchived }) => {
  const isActive = (duration, unit) => {
    return !isSinceStartMode && riskControlScale.duration == duration && riskControlScale.unit === unit;
  };

  return (
    <div style={{ height: 70 }}>
      <div className="bubble-title">{t('risk_control_over_time')}</div>
      <div 
        className={`action-btn content-bubble-btn ${isSinceStartMode ? "action-btn-active" : ""}`} 
        onClick={onSinceStartClick}
      >
        {t('since_start')}
      </div>
      <div 
        className={`action-btn content-bubble-btn ${isActive(12, 'months') ? "action-btn-active" : ""}`} 
        onClick={() => onScaleChange({ duration: 12, unit: 'months' })}
      >
        {t('last_year')}
      </div>
      <div 
        className={`action-btn content-bubble-btn ${isActive(12, 'weeks') ? "action-btn-active" : ""}`} 
        onClick={() => onScaleChange({ duration: 12, unit: 'weeks' })}
      >
        {t('last_3_months')}
      </div>
      <div 
        className={`action-btn content-bubble-btn ${isActive(30, 'days') ? "action-btn-active" : ""}`} 
        onClick={() => onScaleChange({ duration: 30, unit: 'days' })}
      >
        {t('last_month')}
      </div>
      <ArchivedToggle active={showArchived} onToggle={onToggleArchived} t={t} style={{ marginRight: 20 }} />
    </div>
  );
};


const HomePage = () => {
  const { currentOrg, currentProject } = useAppContext();
  // Archived risks are fetched so the risk overview and risk control widgets can opt in to them;
  // every other widget only uses active risks.
  const { data, loading } = useAxiosFetch("/api/risks/" + (currentProject.id ? "byProject/" + currentProject.id : "byOrg/" + currentOrg.id) + "?includeArchived=true");
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [riskControlScale, setRiskControlScale] = useState({ duration: 12, unit: 'months'});
  const [isSinceStartMode, setIsSinceStartMode] = useState(false);
  const [ownerRiskFilter, setOwnerRiskFilter] = useState('all');
  const [dashboardOwnerFilter, setDashboardOwnerFilter] = useState('');
  const [showArchivedInHistory, setShowArchivedInHistory] = useState(false);
  const [showArchivedInOverview, setShowArchivedInOverview] = useState(false);

  const navigateRiskDetails = (riskId, tab) => {
    navigate(`/viewrisk?riskId=${riskId}${tab ? `#${tab}` : ''}`);
  };

  const getRiskLevelClass = (score) => getScoreBorderClass(score, currentOrg.rangeBounds);

  const formatNumber = (number) => {
    if (i18n.language === 'nl') {
      return number.toString().replace('.', ',');
    }
    return number.toString();
  };

  const formatAcceptanceStatus = (daysUntil) => {
    if (daysUntil < 0) return t('expired_since', { count: Math.abs(daysUntil) });
    if (daysUntil === 0) return t('expires_today');
    return t('expires_in_days', { count: daysUntil });
  };

  useEffect(() => {
    // set risk control scale to maximum span of months since the very first risk from data
    if(data && data.length > 0) {
      setRiskControlToMaxSpan();
    }
  }, [data]);

  if (loading) 
    return <LoadPage />;

  const allRisks = data || [];

  // A brand-new register gets a calm getting-started view instead of a grid of empty widgets
  if (allRisks.length === 0) {
    const steps = [
      { title: t('help.step_risks_title'), text: t('dashboard_empty.step_risks') },
      { title: t('help.step_measures_title'), text: t('dashboard_empty.step_measures') },
      { title: t('help.step_evaluate_title'), text: t('dashboard_empty.step_evaluate') },
    ];
    return (
      <div id="content">
        <section className="dash-empty">
          <div className="dash-empty-hero">
            <div className="dash-empty-icon"><i className="icon-Dashboard" aria-hidden="true" /></div>
            <div className="dash-empty-eyebrow">{currentProject.id ? currentProject.name : currentOrg.name}</div>
            <h2>{t('dashboard_empty.title')}</h2>
            <p>{t('dashboard_empty.lead')}</p>
            <div className="dash-empty-actions">
              <button type="button" className="rq-btn rq-btn-primary rq-btn-lg" onClick={() => navigate('/risks', { state: { startAnalysis: true } })}>
                <i className="icon-play" aria-hidden="true" /> {t('start_risk_analysis')}
              </button>
              <button type="button" className="rq-btn rq-btn-secondary rq-btn-lg" onClick={() => navigate('/help')}>
                <i className="icon-idea" aria-hidden="true" /> {t('help.how_title')}
              </button>
            </div>
          </div>
          <ol className="dash-empty-steps">
            {steps.map((step, i) => (
              <li key={step.title} className={i === 0 ? 'is-next' : undefined}>
                <span className="dash-empty-step-nr">{i + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>
    );
  }

  const activeRisks = allRisks.filter(r => !r.archived);
  const matchesOwnerFilter = (r) => !dashboardOwnerFilter || (r.owner || t('unknown')) === dashboardOwnerFilter;
  const historyRisks = (showArchivedInHistory ? allRisks : activeRisks).filter(matchesOwnerFilter);

  const setRiskControlToMaxSpan = (risks = historyRisks) => {
    let firstRiskDate = moment();

    // loop over all risks and their update-reasons date to get the first data point in time
    risks.forEach(risk => {
      (risk["update-reasons"] || []).forEach(update => {
        let updateDate = moment(update.date);
        if (updateDate.isBefore(firstRiskDate)) {
          firstRiskDate = updateDate;
        }
      });
    });
    
    let totalDays = moment().diff(firstRiskDate, 'days');
    let totalMonths = moment().diff(firstRiskDate, 'months');
    let totalWeeks = moment().diff(firstRiskDate, 'weeks');

    // "Since start" should show the actual data span using appropriate units
    // When less than 30 days use 'days', less than 12 months use weeks, otherwise use months
    if (totalDays < 30) {
      // Use days for data spanning less than 30 days
      setRiskControlScale({ duration: Math.max(totalDays, 1), unit: 'days' });
    } else if (totalMonths < 12) {
      // Use weeks for data spanning 30+ days but less than 12 months
      setRiskControlScale({ duration: totalWeeks, unit: 'weeks' });
    } else {
      // Use months for data spanning 12+ months
      setRiskControlScale({ duration: totalMonths, unit: 'months' });
    }
    
    setIsSinceStartMode(true);
  };

  const handleScaleChange = (newScale) => {
    setRiskControlScale(newScale);
    setIsSinceStartMode(false);
  };

  const handleToggleArchivedInHistory = () => {
    const next = !showArchivedInHistory;
    setShowArchivedInHistory(next);
    // Keep "since start" aligned with the first data point of the risks now shown
    if (isSinceStartMode) {
      setRiskControlToMaxSpan((next ? allRisks : activeRisks).filter(matchesOwnerFilter));
    }
  };

  const buildRiskData = (risks) => {
    const result = [
      { name: t('initial'), high: 0, medium: 0, low: 0 },
      { name: t('current'), high: 0, medium: 0, low: 0 },
      { name: t('target'), high: 0, medium: 0, low: 0 }
    ];
    risks.forEach(item => calculateRiskScore(item, result, currentOrg.rangeBounds));
    return result;
  };

  // Owner filter applies to every widget except the owners-by-risk-level widget
  const dashboardOwners = Array.from(new Set(activeRisks.map(r => r.owner || t('unknown')))).sort();
  const filteredData = activeRisks.filter(matchesOwnerFilter);
  const overviewRisks = showArchivedInOverview ? allRisks.filter(matchesOwnerFilter) : filteredData;

  // Shared score (H/M/L) filter for the risk treatment and owners widgets
  const matchesScoreFilter = (risk) => {
    if (ownerRiskFilter === 'all') return true;
    return getRiskSeverity(safeInt(risk['actual-impact']) * safeInt(risk['actual-chance']), currentOrg.rangeBounds) === ownerRiskFilter;
  };

  let measures = 0;
  filteredData.forEach((item) => {
    measures += item.measures?.length;
  });

  const riskData = buildRiskData(filteredData);
  const overviewRiskData = showArchivedInOverview ? buildRiskData(overviewRisks) : riskData;
  const historyRiskData = showArchivedInHistory ? buildRiskData(historyRisks) : riskData;

  let riskHistoryData = [];

  if(historyRisks.length > 0) {
    riskHistoryData = calculateRiskHistory(historyRisks, riskControlScale['duration'], riskControlScale['unit'], historyRiskData[1], currentOrg.rangeBounds);
  }
  
  // Additional KPIs and insights
  const totalRisks = filteredData.length;
  const totalActualScore = filteredData.reduce((sum, r) => sum + (safeInt(r['actual-impact']) * safeInt(r['actual-chance'])), 0);
  const avgActualScore = totalRisks ? (totalActualScore / totalRisks).toFixed(2) : 0;
  const highCount = riskData[1].high;
  const mediumCount = riskData[1].medium;
  const lowCount = riskData[1].low;
  const pct = (n) => totalRisks ? Math.round((n / totalRisks) * 100) : 0;


  // Recent updates across risks
  const recentUpdates = filteredData.flatMap(r => {
    const updates = (r['update-reasons'] || []).slice().sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
    if (updates.length === 0) return [];
    const latest = updates[0];
    const prev = updates[1] || { chance: r['initial-chance'], impact: r['initial-impact'] };
    const latestChance = safeInt(latest.chance);
    const latestImpact = safeInt(latest.impact);
    const prevChance = safeInt(prev.chance);
    const prevImpact = safeInt(prev.impact);
    const latestScore = safeScore(latest.chance, latest.impact);
    const prevScore = safeScore(prev.chance, prev.impact);
    const delta = (latestScore !== null && prevScore !== null) ? latestScore - prevScore : 0;
    return [{
      id: r.id,
      description: r.description,
      owner: r.owner,
      updatedByUser: r.updatedByUser,
      date: latest.date,
      latestScore,
      delta,
      reason: latest.reason,
      latestChance,
      latestImpact,
      prevChance,
      prevImpact
    }];
  }).sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()).slice(0, 7);

  const lastUpdatedAt = recentUpdates[0]?.date || null;

  const ownerRiskTotals = activeRisks.reduce((acc, risk) => {
    const ownerName = risk.owner || t('unknown');
    if (!acc[ownerName]) acc[ownerName] = { low: 0, medium: 0, high: 0 };
    const severity = getRiskSeverity(safeInt(risk['actual-impact']) * safeInt(risk['actual-chance']), currentOrg.rangeBounds);
    acc[ownerName][severity] += 1;
    return acc;
  }, {});

  const severityTotals = Object.values(ownerRiskTotals).reduce((totals, counts) => {
    totals.low += counts.low;
    totals.medium += counts.medium;
    totals.high += counts.high;
    return totals;
  }, { low: 0, medium: 0, high: 0 });

  const ownerPieData = Object.entries(ownerRiskTotals)
    .map(([name, counts]) => {
      if (ownerRiskFilter === 'all') {
        return { name, high: counts.high, medium: counts.medium, low: counts.low };
      }
      let point = { name, high: 0, medium: 0, low: 0 };
      point[ownerRiskFilter] = counts[ownerRiskFilter];
      return point;
    })
    .filter(point => (point.high + point.medium + point.low) > 0)
    .sort((a, b) => (b.high + b.medium + b.low) - (a.high + a.medium + a.low))
    .slice(0, 6);

  const treatmentTotals = filteredData.filter(matchesScoreFilter).reduce((acc, risk) => {
    const key = risk.treatment || 'reduce';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const riskTreatmentMix = ['accept', 'reduce', 'avoid', 'transfer'].map((key) => ({
    key,
    label: t(key),
    count: treatmentTotals[key] || 0,
    percentage: totalRisks ? Math.round(((treatmentTotals[key] || 0) / totalRisks) * 100) : 0
  }));

  const treatmentPieData = riskTreatmentMix
    .filter(item => item.count > 0)
    .map(item => ({ name: item.label, high: item.count, medium: 0, low: 0 }));

  const ownerFilterLabel = (filterKey) => filterKey === 'all' ? t('owner_filter_all') : t(filterKey);

  const temporaryAcceptances = filteredData
    .filter(r => r.treatment === 'accept' && r.tempAcceptTreatment && r.acceptTreatmentDate)
    .map(r => {
      const dueMoment = moment(r.acceptTreatmentDate);
      const daysUntil = dueMoment.startOf('day').diff(moment().startOf('day'), 'days');
      return {
        id: r.id,
        description: r.description,
        owner: r.owner,
        dueMoment,
        daysUntil
      };
    })
    .filter(item => item.daysUntil <= TEMP_ACCEPT_THRESHOLD_DAYS)
    .sort((a, b) => a.daysUntil - b.daysUntil)
    .slice(0, 7);

  // Risk appetite matrix: 5x5 grid of chance (rows) vs impact (cols)
  const riskAppetiteMatrix = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => 0));
  filteredData.forEach(risk => {
    const chance = safeInt(risk['actual-chance']);
    const impact = safeInt(risk['actual-impact']);
    if (chance >= 1 && chance <= 5 && impact >= 1 && impact <= 5) {
      riskAppetiteMatrix[chance - 1][impact - 1]++;
    }
  });

  const getMatrixCellColor = (score) => getRiskColorVar(score, currentOrg.rangeBounds);

  const risksNeedingAttention = filteredData
    .filter(r => r.treatment === 'reduce')
    .map(r => {
      const updates = (r['update-reasons'] || []).slice().sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
      const lastInteraction = updates[0]?.date || r.date;
      const daysSinceUpdate = moment().diff(moment(lastInteraction), 'days');
      return {
        id: r.id,
        description: r.description,
        owner: r.owner,
        lastInteraction,
        daysSinceUpdate,
        score: safeScore(r['actual-impact'], r['actual-chance'])
      };
    })
    .filter(item => item.daysSinceUpdate >= ATTENTION_THRESHOLD_DAYS)
    .sort((a, b) => b.daysSinceUpdate - a.daysSinceUpdate)
    .slice(0, 5);
  
  return (
    <div id="content">
      {/* Dashboard header */}
      <div className="content-bubble no-float" style={{ marginTop: 0, padding: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="bubble-title" style={{ marginBottom: 2 }}>{currentProject.id ? currentProject.name : currentOrg.name}</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>
            {t('risk')} {t('overview')}{lastUpdatedAt ? ` • ${t('updatedAt') || 'updated'} ${moment(lastUpdatedAt).fromNow()}` : ''}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <select className="blue-dropdown" value={dashboardOwnerFilter} onChange={(e) => setDashboardOwnerFilter(e.target.value)}>
            <option value="">{t('owner_without_prefix')} filter</option>
            {dashboardOwners.map(owner => (
              <option key={owner} value={owner}>{owner}</option>
            ))}
          </select>
        </div>
      </div>

   {/* Top KPI row - two bubbles side by side */}
   <div className="home-kpi-grid">
        {/* KPIs 1 */}
        <div className="content-bubble kpi-bubble">
          <div className="kpi-row">
            <div className="kpi-card">
              <i className="icon-Risico kpi-icon" />
              <div className="bubble-title">{t('risks_plural')}</div>
              <div className="risk-bubble" style={{border: 0, fontSize: 32}}>{totalRisks}</div>
            </div>
            <div className="kpi-card">
              <i className="icon-score kpi-icon" />
              <div className="bubble-title">Score</div>
              <div className="risk-bubble" style={{border: 0, fontSize: 32}}>{formatNumber(avgActualScore)}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>{t('average')}</div>
            </div>
            <div className="kpi-card">
              <i className="icon-Maatregelen kpi-icon" />
              <div className="bubble-title">{t('measures')}</div>
              <div className="risk-bubble" style={{border: 0, fontSize: 32}}>{measures}</div>
            </div>
          </div>
        </div>

        {/* KPIs 2 */}
        <div className="content-bubble kpi-bubble">
          <div className="kpi-row">
            <div className="kpi-card kpi-high pointer-class" onClick={() => navigate('/risks?riskScoreFilter=high')}>
              <i className="icon-level-high kpi-icon" />
              <div className="bubble-title">{t('high')}</div>
              <div className="risk-bubble high-risk-bubble">{highCount}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>{pct(highCount)}%</div>
            </div>
            <div className="kpi-card kpi-medium pointer-class" onClick={() => navigate('/risks?riskScoreFilter=medium')}>
              <i className="icon-level-medium kpi-icon" />
              <div className="bubble-title">{t('medium')}</div>
              <div className="risk-bubble medium-risk-bubble">{mediumCount}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>{pct(mediumCount)}%</div>
            </div>
            <div className="kpi-card kpi-low pointer-class" onClick={() => navigate('/risks?riskScoreFilter=low')}>
              <i className="icon-level-low kpi-icon" />
              <div className="bubble-title">{t('low')}</div>
              <div className="risk-bubble low-risk-bubble">{lowCount}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>{pct(lowCount)}%</div>
            </div>
          </div>
        </div>
      </div>

   {/* Risk control over time - full width spanning 2 columns */}
   <div className="content-bubble chart-container" style={{ marginBottom: 32 }}>
        <RiskControlTimeScale
          riskControlScale={riskControlScale}
          onScaleChange={handleScaleChange}
          onSinceStartClick={() => setRiskControlToMaxSpan()}
          isSinceStartMode={isSinceStartMode}
          showArchived={showArchivedInHistory}
          onToggleArchived={handleToggleArchivedInHistory}
          t={t}
        />
        <Chart risksThroughTime={riskHistoryData} />
      </div>

  {/* Main dashboard masonry layout (2 columns ≈ 50% width each) */}
  <div className="home-masonry">

        {/* Top risks */}
        <div className="content-bubble">
          <div style={{ height: 50 }}>
            <div className="bubble-title">{t('current_top_risks')}</div>
          </div>
          <table className="content-bubble-table">
            <thead>
              <tr>
                <td>{t('risk_description')}</td>
                <td>{t('owner')}</td>
                <td width="70">Score</td>
              </tr>
            </thead>
            <tbody>
              {filteredData
                .slice().sort((a, b) => (safeInt(b['actual-impact']) * safeInt(b['actual-chance'])) - (safeInt(a['actual-impact']) * safeInt(a['actual-chance'])))
                .slice(0, 5)
                .map((item, index) => {
                  const topRiskScore = safeScore(item['actual-impact'], item['actual-chance']);
                  return (
                  <tr key={index}>
                    <td className="align-left text-ellipsis pointer-class" onClick={() => navigateRiskDetails(item['id'])}>{index + 1}. {item['description']}</td>
                    <td className="text-ellipsis">{item.owner}</td>
                    <td>

                                          <div className={topRiskScore !== null ? getRiskLevelClass(topRiskScore) : 'score-border-green'}>
                      {topRiskScore !== null ? topRiskScore : '-'}
                    </div>


                    </td>
                  </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Risk overview chart */}
        <div className="content-bubble chart-container">
          <div style={{ height: 50 }}>
            <div className="bubble-title">{t('risk_overview')}</div>
            <ArchivedToggle active={showArchivedInOverview} onToggle={() => setShowArchivedInOverview(v => !v)} t={t} />
          </div>
          <Chart risksThroughTime={overviewRiskData} chartType="Bar" />
        </div>


        {/* Temporary acceptances */}
        <div className="content-bubble">
          <div className="bubble-title" style={{ marginBottom: 12 }}>{t('temporary_acceptances')}</div>
          {temporaryAcceptances.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.7, clear: 'both' }}>{t('no_temporary_acceptances')}</div>
          ) : (
            <table className="content-bubble-table" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <td>{t('risk_description')}</td>
                  <td width="120">{t('owner')}</td>
                  <td width="110">{t('status')}</td>
                </tr>
              </thead>
              <tbody>
                {temporaryAcceptances.map(item => (
                  <tr key={item.id}>
                    <td className="text-ellipsis pointer-class align-left" onClick={() => navigateRiskDetails(item.id)}>
                      {item.description}
                    </td>
                    <td>{item.owner || t('unknown')}</td>
                    <td style={{fontSize: 12}}>{formatAcceptanceStatus(item.daysUntil)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        


        {/* Owners share widget */}
        <div className="content-bubble chart-container">
          <div className="bubble-title" >{t('owners_by_risk_level')}</div>
          {ownerPieData.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.7, textAlign: 'center', padding: 16, clear: 'both' }}>{t('no_owner_data_for_filter')}</div>
          ) : (
            <Chart risksThroughTime={ownerPieData} chartType="Pie" />
          )}
          <div style={{ position: 'relative', zIndex: 20, display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginTop: 12 }}>
            {OWNER_FILTERS.map((key) => (
              <div
                key={key}
                className={`action-btn content-bubble-btn ${ownerRiskFilter === key ? 'action-btn-active' : ''}`}
                style={{width: 80}}
                onClick={() => setOwnerRiskFilter(key)}
              >
                {ownerFilterLabel(key)}
              </div>
            ))}
          </div>
        </div>


        {/* Risk appetite matrix */}
        <div className="content-bubble">
          <div className="bubble-title" style={{ marginBottom: 12 }}>{t('risk_distribution_matrix')}</div>
          <div style={{ position: 'relative', paddingLeft: 24, clear: 'both' }}>
            <div style={{ position: 'absolute', left: -2, top: '50%', transform: 'rotate(-90deg)', transformOrigin: '0 0', fontSize: 12, opacity: 0.7, whiteSpace: 'nowrap' }}>
              {t('impact')} →
            </div>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 3, tableLayout: 'fixed', marginTop: 8 }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  {[1, 2, 3, 4, 5].map(chance => (
                    <th key={chance} style={{ fontSize: 12, padding: 4, textAlign: 'center', opacity: 0.7 }}>{chance}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map(impact => (
                  <tr key={impact}>
                    <td style={{ background: 'none', border: 0, fontSize: 12, padding: 4, textAlign: 'center', fontWeight: 600, opacity: 0.7 }}>{impact}</td>
                    {[1, 2, 3, 4, 5].map(chance => {
                      const score = chance * impact;
                      const count = riskAppetiteMatrix[chance - 1][impact - 1];
                      return (
                        <td key={chance} style={{
                          padding: 6,
                          textAlign: 'center',
                          backgroundColor: getMatrixCellColor(score),
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: 13,
                          borderRadius: 3,
                          opacity: count > 0 ? 1 : 0.45
                        }}>
                          {count > 0
                            ? <div>{count} {count === 1 ? t('risk').toLowerCase() : t('risks_plural').toLowerCase()}</div>
                            : <div style={{ opacity: 0.5 }}>{score}</div>
                          }
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ textAlign: 'center', marginTop: 4, fontSize: 12, opacity: 0.7 }}>
              {t('chance')} →
            </div>
          </div>
        </div>
        
        {/* Recent updates (as table) */}
        <div className="content-bubble break-column">
          <div style={{ height: 50 }}>
            <div className="bubble-title">{t('recent_updates')}</div>
          </div>
          <table className="content-bubble-table" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <td>{t('risk_description')}</td>
                <td width="80" style={{ textAlign: 'right' }}>Score</td>
              </tr>
            </thead>
            <tbody>
              {recentUpdates.length === 0 && (
                <tr>
                  <td colSpan={2}>
                    <div style={{ opacity: 0.7, fontSize: 12, padding: '8px 0' }}>{t('no_updates_yet')}</div>
                  </td>
                </tr>
              )}
              {recentUpdates.map((u) => (
                <tr key={u.id + u.date}>
                  <td className="align-left">
                    <div className="pointer-class" onClick={() => navigateRiskDetails(u.id, 'history')} style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.description}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      {u.updatedByUser || ''} • {moment(u.date).fromNow()}
                    </div>
                    {u.reason && (
                      <div style={{ fontSize: 12, color: 'var(--gray-text-color)', marginTop: 4 }}>
                        {u.reason}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--gray-text-color)', marginTop: 4 }}>
                      {t('chance')}: {u.prevChance || '-'} → {u.latestChance || '-'} • {t('impact')}: {u.prevImpact || '-'} → {u.latestImpact || '-'}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className={u.latestScore !== null ? getRiskLevelClass(u.latestScore) : 'score-border-green'}>
                      {u.latestScore !== null ? u.latestScore : '-'}
                    </div>
                    {u.delta !== 0 && (
                      <div style={{
                        fontSize: 10,
                        color: u.delta > 0 ? 'var(--red-risk-score)' : 'var(--green-risk-score)',
                        marginTop: 4,
                        fontWeight: 600
                      }}>
                        {u.delta > 0 ? '↗' : '↘'} {Math.abs(u.delta)}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* Risk treatment mix */}
        <div className="content-bubble chart-container">
          <div className="bubble-title" >{t('risk_treatment_mix')}</div>
          {treatmentPieData.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.7, textAlign: 'center', padding: 16, clear: 'both' }}>{t('no_results')}</div>
          ) : (
            <Chart risksThroughTime={treatmentPieData} chartType="Pie" />
          )}
          <div style={{ position: 'relative', zIndex: 20, display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginTop: 12 }}>
            {OWNER_FILTERS.map((key) => (
              <div
                key={key}
                className={`action-btn content-bubble-btn ${ownerRiskFilter === key ? 'action-btn-active' : ''}`}
                style={{width: 80}}
                onClick={() => setOwnerRiskFilter(key)}
              >
                {ownerFilterLabel(key)}
              </div>
            ))}
          </div>
        </div>


        {/* Risks needing attention */}
        <div className="content-bubble">
          <div className="bubble-title" style={{ marginBottom: 12 }}>{t('risks_needing_attention')}</div>
          {risksNeedingAttention.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.7, clear: 'both' }}>{t('no_risks_need_attention')}</div>
          ) : (
            <table className="content-bubble-table" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <td>{t('risk_description')}</td>
                  <td width="80">Score</td>
                  <td width="120">{t('last_updated_at')}</td>
                </tr>
              </thead>
              <tbody>
                {risksNeedingAttention.map(item => (
                  <tr key={item.id}>
                    <td className="text-ellipsis pointer-class align-left" onClick={() => navigateRiskDetails(item.id)}>
                      {item.description}
                      <div style={{ fontSize: 11, opacity: 0.7 }}>{item.owner || t('unknown')}</div>
                    </td>
                    <td>
                      <div className={item.score !== null ? getRiskLevelClass(item.score) : 'score-border-green'}>{item.score !== null ? item.score : '-'}</div>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {moment(item.lastInteraction).fromNow()}<br />
                      <span style={{ opacity: 0.7 }}>{t('days_since_update', { count: item.daysSinceUpdate })}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

  </div>
    </div>
  );
};

export default HomePage;
